import { Box, Button, Code, Flex, Link, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as ReactLink, useNavigate } from "react-router";

import { RanaModal } from "@nens/ranalib";

import PersonalApiKeysSvg from "@/assets/personal_api_keys.svg?url";

import { getPersonalApiKeyApi } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { TextInput } from "../../form/TextInput";
import { minLength } from "../../form/validators";
import { Values, useForm } from "../../hooks/useForm";

import styles from "./PersonalApiKeyForm.module.css";

const PersonalApiKeyForm = () => {
  const navigationUrl = `${APP_BASE_HREF}/personal_api_keys`;
  const [apiKeyString, setApiKeyString] = useState("");

  const personalApiKeyApi = getPersonalApiKeyApi();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const onSubmit = async (values: Values) => {
    const body = {
      name: values.name,
      scope: "*:readwrite",
    };

    const createResult = await personalApiKeyApi.personalapikeysCreate({
      data: body,
    });
    setApiKeyString(createResult.key + "");
    queryClient.invalidateQueries({ queryKey: ["personalApiKeys"] });
  };

  const initialValues = {
    name: "",
  };
  const {
    values,
    triedToSubmit,
    handleInputChange,
    handleFocus,
    handleBlur,
    handleSubmit,
    handleReset,
    clearInput,
  } = useForm({
    initialValues,
    onSubmit,
  });

  return (
    <ExplainSideColumn
      imgUrl={PersonalApiKeysSvg}
      imgAltDescription={"Personal-API-key icon"}
      headerText={"Personal Api Keys"}
      explanationText={
        <>
          <Text>
            Personal API keys are used to authenticate external applications with{" "}
            <Link
              isExternal
              color="brand.analytic"
              href="https://en.wikipedia.org/wiki/Basic_access_authentication"
            >
              basic authentication
            </Link>{" "}
            as follows:
          </Text>
          <UnorderedList>
            <ListItem>
              Username is literally __key__ (with double underscores on bothsides of the word "key")
            </ListItem>
            <ListItem>Password is {"{your api key}"}</ListItem>
          </UnorderedList>
          <Text>For example:</Text>
          <Box my={2}>
            <Code>username: __key__</Code>
            <Code>password: example.apikey</Code>
          </Box>
          <Text>
            The API key itself is only visible on creation. It can not be looked up after.
          </Text>
        </>
      }
      backUrl={"/"}
    >
      <form onSubmit={handleSubmit} onReset={handleReset}>
        <TextInput
          title={"Name *"}
          name={"name"}
          value={values.name}
          valueChanged={handleInputChange}
          clearInput={clearInput}
          validated={!minLength(2, values.name)}
          errorMessage={minLength(2, values.name)}
          triedToSubmit={triedToSubmit}
          onFocus={handleFocus}
          onBlur={handleBlur}
          readOnly={false}
        />
        <Flex direction="row" justifyContent={"space-between"}>
          {/* as described here https://chakra-ui.com/docs/components/link */}
          <Button as={ReactLink} to={`${APP_BASE_HREF}/personal_api_keys`}>
            CANCEL
          </Button>

          <Button type="submit" variant="primary">
            Submit
          </Button>
        </Flex>
      </form>
      <RanaModal
        isOpen={apiKeyString !== ""}
        onClose={() => {
          setApiKeyString("");
          navigate(navigationUrl);
        }}
        topBar="Save this key!"
        buttons={[
          <Button
            colorScheme="blue"
            key="close"
            onClick={() => {
              setApiKeyString("");
              navigate(navigationUrl);
            }}
          >
            Close
          </Button>,
        ]}
      >
        <p>
          Save this key somewhere on your computer.
          <br />
          <br />
          Key cannot be retrieved after closing this window.
          <br />
          <br />
        </p>
        <Flex
          justifyContent={"space-between"}
          backgroundColor={"#2fcbab"}
          padding="16px"
          borderWidth="2px"
          borderStyle={"solid"}
          borderColor={"var(--color-header)"}
        >
          <span>{apiKeyString}</span>
          <button
            className={styles.KeyCopyButton}
            onClick={() => {
              navigator.clipboard.writeText(apiKeyString).then(
                () => {
                  // props.addNotification("Copied to clipboard", 2000);
                },
                () => {
                  // props.addNotification("Failed copying ! \nTry manually instead", 2000);
                }
              );
            }}
          >
            <i className={"fa fa-clone"}></i>
          </button>
        </Flex>
      </RanaModal>
    </ExplainSideColumn>
  );
};

export default PersonalApiKeyForm;
